import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { FormControlLabel, Paper, Switch, Typography } from '@material-ui/core';
import { apiFetch } from '../../utils/apiFetch';
import './global/global.css';

const useStyles = makeStyles(() => ({
    paper: {
        padding: '24px',
        marginTop: '20px',
    },
    sectionSubtext: {
        fontSize: '14px',
        color: '#707070',
        marginBottom: '16px',
    },
    settingRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
        marginBottom: '8px',
    },
    label: {
        fontWeight: 500,
    },
    loadError: {
        color: 'red',
        marginTop: '12px',
        fontSize: '14px',
    },
    toggleError: {
        color: 'red',
        marginTop: '12px',
        fontSize: '14px',
    },
}));

const AGENT_TOGGLES = [
    { field: 'deal_coordinator_approval_required', label: 'Deal Coordinator' },
    { field: 'rental_manager_approval_required', label: 'Rental Manager' },
    { field: 'concierge_approval_required', label: 'Concierge' },
    { field: 'marketing_manager_approval_required', label: 'Marketing Manager' },
];

const DEFAULT_CONFIG = {
    deal_coordinator_approval_required: true,
    rental_manager_approval_required: true,
    concierge_approval_required: true,
    marketing_manager_approval_required: true,
    print_orders_live: false,
};

const PRINT_ORDERS_LABEL = 'Place live print orders';

const AdminSiteConfig = () => {
    const classes = useStyles();
    const [config, setConfig] = useState(DEFAULT_CONFIG);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [toggleError, setToggleError] = useState(null);

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const response = await apiFetch(
                    `${process.env.REACT_APP_PAYVIEW_BASE_URL}/api/admin/site-config/`
                );
                if (!response.ok) throw new Error('Failed to load site configuration.');
                const data = await response.json();
                setConfig({
                    deal_coordinator_approval_required: data.deal_coordinator_approval_required,
                    rental_manager_approval_required: data.rental_manager_approval_required,
                    concierge_approval_required: data.concierge_approval_required,
                    marketing_manager_approval_required: data.marketing_manager_approval_required,
                    print_orders_live: data.print_orders_live,
                });
            } catch (err) {
                setLoadError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchConfig();
    }, []);

    const handleToggle = (field) => async (event) => {
        const newValue = event.target.checked;
        const previous = config[field];
        setConfig((prev) => ({ ...prev, [field]: newValue }));
        setToggleError(null);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_PAYVIEW_BASE_URL}/api/admin/site-config/`,
                {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ [field]: newValue }),
                }
            );
            if (!response.ok) throw new Error('Failed to update configuration.');
        } catch (err) {
            setConfig((prev) => ({ ...prev, [field]: previous }));
            setToggleError(err.message);
        }
    };

    return (
        <div className="dashboard-root">
            <div className="db-container">
                <div className="dashboard-container">
                    <div className="dashboard-content">
                        <h3>Site Configuration</h3>
                        <p style={{ color: '#707070' }}>
                            Runtime settings that take effect immediately without a deploy.
                        </p>
                    </div>
                    {loadError && !loading && (
                        <Typography className={classes.loadError}>{loadError}</Typography>
                    )}
                    <Paper className={classes.paper} elevation={1}>
                        <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: '4px' }}>
                            Email approval required
                        </Typography>
                        <Typography className={classes.sectionSubtext}>
                            When enabled, outbound emails from the selected agent are held in the
                            approval queue for staff review before sending.
                        </Typography>
                        {AGENT_TOGGLES.map(({ field, label }) => (
                            <div key={field} className={classes.settingRow}>
                                <Typography className={classes.label}>{label}</Typography>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={config[field]}
                                            onChange={handleToggle(field)}
                                            disabled={loading}
                                            color="primary"
                                            inputProps={{ 'aria-label': label }}
                                        />
                                    }
                                    label={null}
                                />
                            </div>
                        ))}
                    </Paper>
                    <Paper className={classes.paper} elevation={1}>
                        <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: '4px' }}>
                            Flyer print orders
                        </Typography>
                        <Typography className={classes.sectionSubtext}>
                            When off, print orders are sent to Gelato as drafts, which are never
                            printed or billed. When on, orders are printed, shipped, and billed to
                            FastRealty&apos;s Gelato account.
                        </Typography>
                        <div className={classes.settingRow}>
                            <Typography className={classes.label}>{PRINT_ORDERS_LABEL}</Typography>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={config.print_orders_live}
                                        onChange={handleToggle('print_orders_live')}
                                        disabled={loading}
                                        color="primary"
                                        inputProps={{ 'aria-label': PRINT_ORDERS_LABEL }}
                                    />
                                }
                                label={null}
                            />
                        </div>
                    </Paper>
                    {toggleError && (
                        <Typography className={classes.toggleError}>{toggleError}</Typography>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminSiteConfig;
